const enc = new TextEncoder();
export const KEY_WRAP_INFO = "rightra-key-wrap-v1";

export function generateContentKey() {
  return crypto.getRandomValues(new Uint8Array(32));
}

export function bytesToBase64(bytes) {
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

export const exportKeyBase64 = bytesToBase64;

export function base64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export async function importAesKey(keyBytes, usages = ["encrypt", "decrypt"]) {
  return crypto.subtle.importKey("raw", keyBytes, { name: "AES-GCM" }, false, usages);
}

export function segmentAad(contentId, index) {
  return enc.encode(`rightra:v2|${contentId}|${index}`);
}

/**
 * Encrypt one segment from a source Blob. Streaming path: the SDK calls this
 * once per segment and releases the result after upload, so peak memory stays
 * at one segment regardless of file size.
 */
export async function encryptSegmentAt(blob, part, key, contentId, index) {
  const plain = await blob.slice(part.start, part.end).arrayBuffer();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: segmentAad(contentId, index) },
    key,
    plain
  );
  return { encrypted: new Uint8Array(encrypted), iv };
}

/**
 * Encrypts byte ranges of a Blob, one AES-256-GCM segment per range.
 * Kept for compatibility with existing callers. Prefer encryptSegmentAt
 * for large files.
 */
export async function encryptBlobParts(blob, parts, keyBytes, contentId, onProgress) {
  const key = await importAesKey(keyBytes, ["encrypt"]);
  const segments = [];
  const ivs = [];
  for (let i = 0; i < parts.length; i++) {
    const { encrypted, iv } = await encryptSegmentAt(blob, parts[i], key, contentId, i);
    segments.push(encrypted);
    ivs.push(iv);
    if (onProgress) onProgress(i + 1, parts.length);
  }
  return { segments, ivs };
}

export async function decryptSegmentBuffer(key, segmentBuffer, iv, aad) {
  const params = { name: "AES-GCM", iv };
  if (aad) params.additionalData = aad;
  return new Uint8Array(await crypto.subtle.decrypt(params, key, segmentBuffer));
}

export async function createKeyExchange() {
  const pair = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, [
    "deriveBits",
  ]);
  const raw = new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey));
  return { privateKey: pair.privateKey, publicKeyB64: bytesToBase64(raw) };
}

export async function unwrapSessionKey({ privateKey, wrapped, sessionId }) {
  const serverPub = await crypto.subtle.importKey(
    "raw",
    base64ToBytes(wrapped.server_public_key),
    { name: "ECDH", namedCurve: "P-256" },
    false,
    []
  );
  const secret = await crypto.subtle.deriveBits({ name: "ECDH", public: serverPub }, privateKey, 256);
  const hkdfKey = await crypto.subtle.importKey("raw", secret, "HKDF", false, ["deriveKey"]);
  const wrapKey = await crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: enc.encode(sessionId), info: enc.encode(KEY_WRAP_INFO) },
    hkdfKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"]
  );
  const raw = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: base64ToBytes(wrapped.iv), additionalData: enc.encode(sessionId) },
    wrapKey,
    base64ToBytes(wrapped.ciphertext)
  );
  const key = new Uint8Array(raw);
  if (key.length !== 32) throw new Error("Unwrapped key has the wrong length.");
  return key;
}